import React, { useState } from 'react';
import HMSButton from './HMSButton';
import './HeaderNameEditor.css';

/**
 * The name of a deal, recap, delivery or budget, as a person reads and edits it.
 *
 * IMT auto-naming (go-live card 1248h535zfw; Chris 2026-10-19). The server is
 * the one home for the name (backend/offer/deal_names.py): each record's answer
 * carries ``automatic_name`` (what it is called with no typed name) and
 * ``display_name`` (the typed name, else the automatic one). Only a TYPED name
 * is stored, and an empty one means automatic, so the automatic name follows
 * every later change until someone types their own.
 *
 * So a screen never writes the automatic name into the typed field -- copying
 * it in on click is what froze names before. Editing starts from the typed
 * name, or empty with the automatic name as the grey hint, and "Use automatic
 * name" empties the typed name so the automatic one comes back.
 */

/** The name on screen: the typed name, else the automatic one. Once saved this
 *  is the server's ``display_name``; before that it already shows a typed
 *  change (or a cleared one) the person has not saved yet. */
export const shownName = (typedName, automaticName) => (
  (typedName || '').trim() || automaticName || ''
);

/** One click back to the automatic name. Only offered when a typed name exists
 *  and there is an automatic name to come back to. */
export const UseAutomaticNameButton = ({ typedName, automaticName, onUseAutomatic, className = '' }) => {
  if (!(typedName || '').trim() || !automaticName) return null;
  return (
    <HMSButton
      variant="secondary"
      size="compact"
      className={className}
      onClick={onUseAutomatic}
      title={`Back to the automatic name: ${automaticName}`}
    >
      Use automatic name
    </HMSButton>
  );
};

/**
 * The click-to-edit name in a record's green header (deal, recap, delivery).
 *
 * @param {string}   typedName          - the typed name, '' when automatic
 * @param {string}   automaticName      - the server's ``automatic_name``
 * @param {Function} onTypedNameChange  - called with the new typed name ('' = automatic)
 * @param {string}   emptyLabel         - shown when there is no name of either kind yet
 *                                        (a record the server has not named)
 * @param {string}   inputClassName / displayClassName / iconClassName - the screen's own look
 */
export default function HeaderNameEditor({
  typedName,
  automaticName,
  onTypedNameChange,
  emptyLabel,
  inputClassName,
  displayClassName,
  iconClassName,
}) {
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing) {
    return (
      <input
        type="text"
        value={typedName}
        onChange={(e) => onTypedNameChange(e.target.value)}
        className={inputClassName}
        placeholder={automaticName || emptyLabel}
        autoFocus
        onBlur={() => setIsEditing(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setIsEditing(false);
          }
        }}
      />
    );
  }

  return (
    <>
      <span className={displayClassName} onClick={() => setIsEditing(true)}>
        {shownName(typedName, automaticName) || emptyLabel}
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={iconClassName}
        >
          <path
            d="M14.06 9.02L14.98 9.94L5.92 19H5V18.08L14.06 9.02ZM17.66 3C17.41 3 17.15 3.1 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C18.17 3.09 17.92 3 17.66 3ZM14.06 6.19L3 17.25V21H6.75L17.81 9.94L14.06 6.19Z"
            fill="currentColor"
          />
        </svg>
      </span>
      <UseAutomaticNameButton
        typedName={typedName}
        automaticName={automaticName}
        onUseAutomatic={() => onTypedNameChange('')}
        className="record-name-use-automatic-beside"
      />
    </>
  );
}
